import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { SupplierService } from '../../../supplier/supplier.service';
import { SupplierProfile } from '../../../supplier/supplier.type';

/**
 * Supplier picker (search by name or username) shared by the payment and
 * report pages. Searches server-side as the user types, since the supplier
 * list is too large to load in one page. Emits the chosen supplier's user id, or '' once cleared.
 */
@Component({
  selector: 'app-supplier-filter',
  imports: [
    FormsModule, MatAutocompleteModule, MatButtonModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatTooltipModule,
  ],
  template: `
    <mat-form-field appearance="outline" class="supplier-field" subscriptSizing="dynamic">
      <mat-label>Supplier</mat-label>
      <mat-icon matPrefix class="prefix-icon">person_search</mat-icon>
      <input matInput
        [(ngModel)]="searchText"
        (ngModelChange)="onTextChange($event)"
        [matAutocomplete]="auto"
        placeholder="Search name or username">
      @if (supplierId) {
      <button matSuffix mat-icon-button type="button" matTooltip="Clear supplier filter" (click)="clear()">
        <mat-icon>close</mat-icon>
      </button>
      }
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="display" (optionSelected)="onSelected($event)">
        @for (s of suppliers; track s.user_id) {
        <mat-option [value]="s">
          {{ s.name }} <span class="opt-username">&#64;{{ s.username }}</span>
        </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  `,
  styles: `
    :host { display: block; }
    .supplier-field { width: 100%; }
    .prefix-icon { margin-inline: 12px 4px; opacity: 0.7; }
    .opt-username { opacity: 0.65; font-size: 0.85em; }
  `,
})
export class SupplierFilter implements OnInit, OnChanges {
  private static readonly SEARCH_DEBOUNCE_MS = 300;
  private static readonly PAGE_SIZE = 20;

  private supplierService = inject(SupplierService);
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<string>();
  /** The supplier behind the applied id, kept so its label survives new searches. */
  private selected: SupplierProfile | null = null;

  /** The applied supplier id; lets the page reset or preselect it (e.g. from the URL). */
  @Input() supplierId = '';
  @Output() supplierIdChange = new EventEmitter<string>();

  suppliers: SupplierProfile[] = [];
  /** Display string bound to the input — not the filter value. */
  searchText = '';

  ngOnInit(): void {
    // switchMap drops the response of any search that a newer keystroke superseded.
    this.search$.pipe(
      map(q => q.trim()),
      debounceTime(SupplierFilter.SEARCH_DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap(q => this.supplierService
        .listSuppliers({ search: q || undefined, pageSize: SupplierFilter.PAGE_SIZE })
        .pipe(catchError(() => of(null)))), // the filter is optional
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(res => { if (res) this.suppliers = res.suppliers; });

    // Initial suggestions before anything is typed.
    this.supplierService.listSuppliers({ pageSize: SupplierFilter.PAGE_SIZE })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => { this.suppliers = res.suppliers; },
        error: () => { /* the filter is optional */ },
      });
    this.syncText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['supplierId'] && !changes['supplierId'].firstChange) this.syncText();
  }

  /** Keeps the visible text in step with an id set from outside. */
  private syncText(): void {
    if (!this.supplierId) {
      this.selected = null;
      this.searchText = '';
      return;
    }
    if (this.selected?.user_id === this.supplierId) {
      this.searchText = this.label(this.selected);
      return;
    }
    // The preselected supplier (e.g. from the URL) may not be in the current results.
    this.supplierService.getSupplier(this.supplierId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: s => {
          if (s.user_id !== this.supplierId) return;
          this.selected = s;
          this.searchText = this.label(s);
        },
        error: () => { /* leave the text empty; the id still applies */ },
      });
  }

  private label(s: SupplierProfile): string {
    return `${s.name} (@${s.username})`;
  }

  onSelected(event: MatAutocompleteSelectedEvent): void {
    const s = event.option.value as SupplierProfile;
    this.selected = s;
    this.searchText = this.label(s);
    this.supplierId = s.user_id;
    this.supplierIdChange.emit(s.user_id);
  }

  clear(): void {
    this.selected = null;
    this.searchText = '';
    this.supplierId = '';
    this.supplierIdChange.emit('');
  }

  display = (s: SupplierProfile | string | null): string =>
    typeof s === 'string' ? s : s ? this.label(s) : '';

  /**
   * Typing without picking an option no longer names a specific supplier.
   * Picking an option also reports a change (with the option object rather
   * than text) — that one is handled by onSelected instead.
   */
  onTextChange(value: unknown): void {
    if (typeof value !== 'string') return;
    if (this.supplierId) {
      this.selected = null;
      this.supplierId = '';
      this.supplierIdChange.emit('');
    }
    this.search$.next(value);
  }
}
